import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, ConfigProvider, Form, Typography, Space } from 'antd'
import {
  Poll as PollType,
  PollAnswerItem,
  PollFormValue,
  PollQuestion as PollQuestionType,
  getOptionValue,
  isOptionRequired,
} from '../types'
import { interpolatePollText } from '../interpolate'
import type { PollRequisitePatch } from '../dadata-party'
import { PollInnField } from './PollInnField'
import { PollBikField } from './PollBikField'
import { PollQuestion } from './PollQuestion'
import { PollStepsHeader } from './PollStepsHeader'

const { Title } = Typography

interface PollProps {
  quiz: PollType
  initialAnswers?: PollAnswerItem[]
  onSubmit: (answers: PollAnswerItem[]) => void
  onSave?: (answers: PollAnswerItem[]) => void
  loading?: boolean
  submitText?: string
  saveText?: string
  showSaveButton?: boolean
  /** Внешний контекст для плейсхолдеров {{name}} / {{имя}} / {{brand_name}} … */
  variables?: Record<string, string | null | undefined>
  /**
   * Значения по умолчанию по system_key (например, brand_name из регистрации).
   * Подставляются только в пустые поля, пользователь может их изменить.
   */
  prefill?: Record<string, string | null | undefined>
}

/** Подписи и поля анкеты: крупнее, обычное начертание, основной цвет текста */
const LABEL_STYLE: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 400,
  color: 'rgba(0, 0, 0, 0.88)',
  lineHeight: 1.4,
}

const FORM_THEME = {
  components: {
    Form: { labelFontSize: 16, labelColor: 'rgba(0, 0, 0, 0.88)', verticalLabelPadding: '0 0 6px' },
    Input: { inputFontSize: 16, inputFontSizeLG: 16 },
    InputNumber: { inputFontSize: 16, inputFontSizeLG: 16 },
    Select: { fontSize: 16, fontSizeLG: 16 },
    Radio: { fontSize: 15 },
  },
}

export const Poll: React.FC<PollProps> = ({
  quiz,
  initialAnswers = [],
  onSubmit,
  onSave,
  loading = false,
  submitText = 'Отправить',
  saveText = 'Сохранить черновик',
  showSaveButton = false,
  variables,
  prefill,
}) => {
  const [form] = Form.useForm()
  const [currentStep, setCurrentStep] = useState(0)

  const initialValues = useMemo(() => {
    const values: Record<string, PollFormValue> = {}
    if (quiz.questions) {
      quiz.questions.forEach(question => {
        const answer = initialAnswers.find(
          a => a.question_id === question.id || a.question === question.title,
        )?.answer
        const systemKey = question.config?.system_key
          ? String(question.config.system_key)
          : /бренд/i.test(question.title || '')
          ? 'brand_name'
          : ''
        const prefillValue = systemKey ? String(prefill?.[systemKey] ?? '').trim() : ''
        values[question.id] =
          isEmptyValue(answer ?? null) && prefillValue
            ? prefillValue
            : answer ?? getDefaultValue(question)
      })
    }
    return values
  }, [quiz, initialAnswers, prefill])

  const [formValues, setFormValues] = useState<Record<string, PollFormValue>>(initialValues)

  useEffect(() => {
    setFormValues(initialValues)
  }, [initialValues])

  const steps = useMemo(() => {
    const questions = quiz.questions || []
    const sortQuestions = (list: PollQuestionType[]) =>
      [...list].sort((a, b) => {
        const byOrder = (a.order_index ?? 0) - (b.order_index ?? 0)
        if (byOrder !== 0) return byOrder
        // ИНН всегда выше остальных реквизитов при равном order_index
        const aInn = a.config?.system_key === 'requisite.inn' ? 0 : 1
        const bInn = b.config?.system_key === 'requisite.inn' ? 0 : 1
        return aInn - bInn
      })

    if (quiz.steps && quiz.steps.length > 0) {
      const ordered = [...quiz.steps].sort((a, b) => a.order_index - b.order_index)
      return ordered.map(step => ({
        ...step,
        questions: sortQuestions(questions.filter(q => q.step_id === step.id)),
      }))
    }
    return [
      {
        id: null,
        title: 'Вопросы',
        description: null,
        order_index: 0,
        questions: sortQuestions(questions),
      },
    ]
  }, [quiz])

  const currentQuestions = steps[currentStep]?.questions || []

  const questionIdBySystemKey = useMemo(() => {
    const map = new Map<string, string>()
    for (const question of quiz.questions || []) {
      const key = question.config?.system_key
      if (key) map.set(String(key), question.id)
    }
    return map
  }, [quiz.questions])

  const applyPartyPatch = useCallback(
    (patch: PollRequisitePatch) => {
      const next: Record<string, string> = {}
      for (const [systemKey, value] of Object.entries(patch)) {
        if (value == null || value === '') continue
        const questionId = questionIdBySystemKey.get(systemKey)
        if (questionId) next[questionId] = value
      }
      if (!Object.keys(next).length) return
      form.setFieldsValue(next)
      setFormValues(prev => ({ ...prev, ...next }))
    },
    [form, questionIdBySystemKey],
  )

  const resolvedVariables = useMemo(() => {
    const vars: Record<string, string> = {}
    if (variables) {
      for (const [key, value] of Object.entries(variables)) {
        if (value == null) continue
        const trimmed = String(value).trim()
        if (trimmed) vars[key] = trimmed
      }
    }
    for (const question of quiz.questions || []) {
      const systemKey = question.config?.system_key
      if (!systemKey) continue
      const raw = formValues[question.id]
      if (raw == null || Array.isArray(raw)) continue
      const text = String(raw).trim()
      if (!text) continue
      vars[systemKey] = text
      if (systemKey === 'brand_name') {
        vars['бренд'] = text
      }
    }
    return vars
  }, [variables, formValues, quiz.questions])

  const buildAnswers = (values: Record<string, PollFormValue>): PollAnswerItem[] =>
    (quiz.questions || []).map(question => {
      let answer = values[question.id]
      if (question.type === 'checkbox') {
        const selected = Array.isArray(answer) ? answer.map(String) : []
        const required = question.options.filter(isOptionRequired).map(getOptionValue)
        answer = [...new Set([...required, ...selected])]
      }
      const labels = resolveAnswerLabels(question, answer)
      return {
        question_id: question.id,
        system_key: question.config?.system_key || null,
        question: question.title,
        answer,
        answer_labels: labels,
        field_type: question.type,
      }
    })

  const handleSubmit = () => {
    // true — все значения, включая поля предыдущих шагов (Form.Item размонтированы)
    const values = form.getFieldsValue(true) as Record<string, PollFormValue>
    onSubmit(buildAnswers(values))
  }

  const handleSave = () => {
    const values = form.getFieldsValue(true) as Record<string, PollFormValue>
    if (onSave) {
      onSave(buildAnswers(values))
    }
  }

  const isLastStep = currentStep >= steps.length - 1

  /** Enter в инпуте сабмитит форму — на промежуточных шагах это «Далее», не финальная отправка */
  const handleFinish = () => {
    if (!isLastStep) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1))
      return
    }
    handleSubmit()
  }

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }

  const pollTitle = interpolatePollText(quiz.title, resolvedVariables)
  const pollDescription = interpolatePollText(quiz.description, resolvedVariables)
  const stepDescription = interpolatePollText(steps[currentStep]?.description, resolvedVariables)

  return (
    <div>
      <Title level={3}>{pollTitle}</Title>
      {pollDescription ? <Typography.Paragraph>{pollDescription}</Typography.Paragraph> : null}

      {steps.length > 1 && (
        <PollStepsHeader
          current={currentStep}
          titles={steps.map(step => interpolatePollText(step.title, resolvedVariables) || '')}
        />
      )}

      {stepDescription ? (
        <Alert
          type="info"
          showIcon
          message={
            <span
              style={{
                color: 'rgba(0, 0, 0, 0.88)',
                fontSize: 15,
                fontWeight: 500,
                lineHeight: 1.45,
              }}
            >
              {stepDescription}
            </span>
          }
          style={{ marginBottom: 20 }}
        />
      ) : null}

      <ConfigProvider theme={FORM_THEME}>
        <Form
          form={form}
          layout="vertical"
          size="large"
          initialValues={initialValues}
          onValuesChange={(_, all) => setFormValues(all as Record<string, PollFormValue>)}
          onFinish={handleFinish}
          autoComplete="off"
        >
          {currentQuestions.map(question => (
            <Form.Item
              key={question.id}
              name={question.id}
              label={<span style={LABEL_STYLE}>{question.title}</span>}
              extra={question.subtitle || undefined}
              required={question.is_required}
              rules={
                question.is_required
                  ? [
                      {
                        validator: (_, value) => {
                          if (isEmptyValue(value)) {
                            return Promise.reject(new Error('Это поле обязательно для заполнения'))
                          }
                          return Promise.resolve()
                        },
                      },
                    ]
                  : undefined
              }
            >
              {question.config?.system_key === 'requisite.inn' ? (
                <PollInnField onPartyFill={applyPartyPatch} />
              ) : question.config?.system_key === 'requisite.bik' ? (
                <PollBikField onBankFill={applyPartyPatch} />
              ) : (
                <PollQuestion question={question} hideTitle />
              )}
            </Form.Item>
          ))}

          <Form.Item>
            <Space>
              {currentStep > 0 && (
                <Button htmlType="button" onClick={handlePrev} disabled={loading}>
                  Назад
                </Button>
              )}
              <Button type="primary" htmlType="submit" loading={loading}>
                {isLastStep ? submitText : 'Далее'}
              </Button>
              {showSaveButton && onSave && (
                <Button htmlType="button" onClick={handleSave} loading={loading}>
                  {saveText}
                </Button>
              )}
            </Space>
          </Form.Item>
        </Form>
      </ConfigProvider>
    </div>
  )
}

function getDefaultValue(question: PollQuestionType): PollFormValue {
  switch (question.type) {
    case 'checkbox':
      return question.options.filter(isOptionRequired).map(getOptionValue)
    case 'switch':
      return false
    case 'number':
      return null
    default:
      return ''
  }
}

function resolveAnswerLabels(
  question: PollQuestionType,
  answer: PollFormValue,
): string | string[] | null {
  if (question.type === 'checkbox' && Array.isArray(answer)) {
    return answer.map(
      code => question.options.find(o => getOptionValue(o) === code)?.label || String(code),
    )
  }
  if (typeof answer === 'string') {
    return question.options.find(o => getOptionValue(o) === answer)?.label || answer
  }
  return answer == null ? null : String(answer)
}

function isEmptyValue(value: PollFormValue): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string' && value.trim() === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

export default Poll
